import { PlaneTakeoff, Sparkles, CalendarDays, Lightbulb } from "lucide-react";

/* Step-by-step "Import your travel history" instructions shown on the MTH page.
   Key user education: TripIt's calendar feed only carries UPCOMING trips — past
   history comes in via the AI import (paste / PDF / screenshot). */
const Step = ({ n, children }) => (
  <li className="flex gap-2.5 text-sm text-zinc-700">
    <span className="shrink-0 w-5 h-5 rounded-full bg-orange-600 text-white text-[11px] font-800 flex items-center justify-center mt-0.5">{n}</span>
    <span>{children}</span>
  </li>
);

export default function ImportGuide() {
  return (
    <details className="bg-white border border-zinc-200 mt-4" data-testid="import-guide">
      <summary className="px-4 py-3 cursor-pointer font-heading font-900 text-sm hover:bg-zinc-50 flex items-center gap-2">
        <PlaneTakeoff className="w-4 h-4 text-orange-600" />
        How to import your travel history (TripIt &amp; others) — step by step
      </summary>
      <div className="px-5 pb-5 grid md:grid-cols-2 gap-6 pt-2">
        <div data-testid="import-guide-ics">
          <h3 className="font-heading font-800 text-sm flex items-center gap-1.5 mb-2">
            <CalendarDays className="w-4 h-4 text-orange-600" /> Method 1 — Calendar file (.ics)
            <span className="text-[10px] font-800 uppercase bg-orange-100 text-orange-700 px-1.5 py-0.5">best for upcoming trips</span>
          </h3>
          <ol className="space-y-2">
            <Step n={1}>Sign in at <b>tripit.com</b> in a web browser (not the app)</Step>
            <Step n={2}>Click your name → <b>Settings</b> → <b>Calendar Feed</b> and copy the <b>webcal://</b> link</Step>
            <Step n={3}>Paste the link in a new browser tab and change <b>webcal://</b> to <b>https://</b> — a .ics file downloads</Step>
            <Step n={4}>Here in <b>My Trips</b>, click <b>Import → Calendar file (.ics)</b> and upload it</Step>
            <Step n={5}>Review the detected trips, rename or untick any, then click <b>Import</b></Step>
          </ol>
          <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2.5 flex gap-1.5">
            <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" />
            Only a few trips in your file? That's normal — TripIt's calendar feed usually contains
            <b>&nbsp;upcoming and recent trips only</b>. For past trips, use Method 2.
          </p>
        </div>
        <div data-testid="import-guide-ai">
          <h3 className="font-heading font-800 text-sm flex items-center gap-1.5 mb-2">
            <Sparkles className="w-4 h-4 text-violet-600" /> Method 2 — AI import
            <span className="text-[10px] font-800 uppercase bg-violet-100 text-violet-700 px-1.5 py-0.5">best for past trips</span>
          </h3>
          <ol className="space-y-2">
            <Step n={1}>Open any <b>past trip</b> in TripIt (app or website)</Step>
            <Step n={2}>Copy the itinerary text — or take a <b>screenshot</b>, or use Print → <b>Save as PDF</b></Step>
            <Step n={3}>Here in <b>My Trips</b>, click <b>Import → AI import (paste / upload)</b></Step>
            <Step n={4}>Paste the text, or upload the PDF / screenshot (JPG, PNG — up to 8&nbsp;MB)</Step>
            <Step n={5}>Click <b>Extract trips</b>, check the preview, then <b>Import</b></Step>
          </ol>
          <p className="mt-3 text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 p-2.5">
            <b>Tips:</b> one trip at a time works best · include dates and flight numbers · screenshots
            must be readable. Also works great with <b>airline and hotel confirmation emails</b> — just
            paste them in. Everything imports as private trips you can edit or delete.
          </p>
        </div>
      </div>
    </details>
  );
}
